import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";

interface Row {
    disorder: string;
    drug: string;
    gene?: string | null;
    target_name?: string | null;
    uniprot?: string | null;
}

interface TargetInfo {
    gene?: string | null;
    target_name?: string | null;
    uniprot?: string | null;
}

interface TreeNode {
    name: string;
    level: number; // main: 0=root,1=disorder,2=drug · detail: 0=drug,1=disorder,2=target
    children: TreeNode[];
    disorder?: string;
    drug?: string;
    gene?: string | null;
    target_name?: string | null;
    uniprot?: string | null;
    angle?: number;
    radius?: number;
    leafIndex?: number;
}

const DISORDER_PALETTE = [
    '#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd',
    '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf',
];

const SQL = `SELECT DISTINCT dis.name AS disorder, d.drug_name AS drug,
    t.gene_name AS gene, t.target_name AS target_name, t.uniprot_id AS uniprot
FROM disorders dis
JOIN drugs d ON d.disorder_id = dis.id
LEFT JOIN targets t ON t.drug_id = d.id
WHERE d.drug_name IS NOT NULL AND d.drug_name <> ''
ORDER BY dis.name, d.drug_name`;

function buildHierarchy(rows: Row[], disorderFilter: Set<string>): TreeNode {
    const root: TreeNode = { name: 'MintDB', level: 0, children: [] };
    const disMap = new Map<string, TreeNode>();

    for (const r of rows) {
        if (!r.disorder || !r.drug) continue;
        if (disorderFilter.size && !disorderFilter.has(r.disorder)) continue;

        let d = disMap.get(r.disorder);
        if (!d) {
            d = { name: r.disorder, level: 1, children: [], disorder: r.disorder };
            disMap.set(r.disorder, d);
            root.children.push(d);
        }
        if (!d.children.find(c => c.name === r.drug)) {
            d.children.push({ name: r.drug, level: 2, children: [], disorder: r.disorder, drug: r.drug });
        }
    }
    return root;
}

// Drug-focused hierarchy: root=drug, level-1=disorders the drug is in, level-2=targets.
function buildDrugHierarchy(rows: Row[], drugName: string): TreeNode {
    const root: TreeNode = { name: drugName, level: 0, children: [], drug: drugName };
    const disMap = new Map<string, TreeNode>();

    for (const r of rows) {
        if (r.drug !== drugName || !r.disorder) continue;

        let d = disMap.get(r.disorder);
        if (!d) {
            d = { name: r.disorder, level: 1, children: [], disorder: r.disorder, drug: drugName };
            disMap.set(r.disorder, d);
            root.children.push(d);
        }
        const label = r.gene || r.target_name;
        if (!label) continue;
        if (!d.children.find(c => c.name === label)) {
            d.children.push({
                name: label,
                level: 2,
                children: [],
                disorder: r.disorder,
                drug: drugName,
                gene: r.gene,
                target_name: r.target_name,
                uniprot: r.uniprot,
            });
        }
    }
    return root;
}

function layoutRadial(root: TreeNode, radius: number) {
    let leafIdx = 0;
    const assignLeaf = (n: TreeNode) => {
        if (n.children.length === 0) {
            n.leafIndex = leafIdx++;
        } else {
            n.children.forEach(assignLeaf);
        }
    };
    assignLeaf(root);
    const total = Math.max(1, leafIdx);

    const computeAngle = (n: TreeNode): number => {
        if (n.children.length === 0) {
            n.angle = ((n.leafIndex! + 0.5) / total) * Math.PI * 2;
        } else {
            const angles = n.children.map(computeAngle);
            n.angle = (Math.min(...angles) + Math.max(...angles)) / 2;
        }
        return n.angle!;
    };
    computeAngle(root);

    const assignRadius = (n: TreeNode) => {
        n.radius = (n.level / 2) * radius;
        n.children.forEach(assignRadius);
    };
    assignRadius(root);

    return { totalLeaves: total };
}

const polar = (r: number, a: number) => [r * Math.cos(a), r * Math.sin(a)] as const;

function radialLink(p: TreeNode, c: TreeNode): string {
    const [x1, y1] = polar(p.radius!, p.angle!);
    const [x2, y2] = polar(c.radius!, c.angle!);
    const rMid = (p.radius! + c.radius!) / 2;
    const [cx1, cy1] = polar(rMid, p.angle!);
    const [cx2, cy2] = polar(rMid, c.angle!);
    return `M${x1.toFixed(2)},${y1.toFixed(2)} C${cx1.toFixed(2)},${cy1.toFixed(2)} ${cx2.toFixed(2)},${cy2.toFixed(2)} ${x2.toFixed(2)},${y2.toFixed(2)}`;
}

function bundledLink(a: TreeNode, b: TreeNode): string {
    const [x1, y1] = polar(a.radius!, a.angle!);
    const [x2, y2] = polar(b.radius!, b.angle!);
    // Quadratic bezier with control pulled toward the centre — bundles the
    // shared-drug links inward so they don't overlap the radial tree edges.
    const mx = (x1 + x2) / 2;
    const my = (y1 + y2) / 2;
    const cxp = mx * 0.15;
    const cyp = my * 0.15;
    return `M${x1.toFixed(2)},${y1.toFixed(2)} Q${cxp.toFixed(2)},${cyp.toFixed(2)} ${x2.toFixed(2)},${y2.toFixed(2)}`;
}

function arcPath(rIn: number, rOut: number, a0: number, a1: number): string {
    const large = a1 - a0 > Math.PI ? 1 : 0;
    const [x0o, y0o] = polar(rOut, a0);
    const [x1o, y1o] = polar(rOut, a1);
    const [x0i, y0i] = polar(rIn, a0);
    const [x1i, y1i] = polar(rIn, a1);
    return `M${x0o},${y0o} A${rOut},${rOut} 0 ${large} 1 ${x1o},${y1o} L${x1i},${y1i} A${rIn},${rIn} 0 ${large} 0 ${x0i},${y0i} Z`;
}

const MintDBTree: React.FC = () => {
    const [rows, setRows] = useState<Row[] | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);


    const wrapperRef = useRef<HTMLDivElement>(null);
    const svgRef = useRef<SVGSVGElement>(null);
    const zoomGroupRef = useRef<SVGGElement>(null);
    const [size, setSize] = useState(900);

    const transformRef = useRef({ k: 1, x: 0, y: 0 });

    const [hover, setHover] = useState<{
        x: number; y: number;
        title: string;
        body: string[];
    } | null>(null);

    const [highlightedDrug, setHighlightedDrug] = useState<string | null>(null);
    const [highlightedDisorder, setHighlightedDisorder] = useState<string | null>(null);

    const [selectedLeaf, setSelectedLeaf] = useState<{ drug: string; disorder: string } | null>(null);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const res = await fetch('/api/execute-query', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ query: SQL }),
                });
                const json = await res.json();
                if (!res.ok || !json.success) {
                    throw new Error(json.error || json.hint || `HTTP ${res.status}`);
                }
                if (!cancelled) setRows(json.results || []);
            } catch (e: any) {
                if (!cancelled) setError(e.message || 'Failed to load tree data');
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        const onResize = () => {
            if (wrapperRef.current) {
                const w = wrapperRef.current.clientWidth;
                setSize(Math.max(380, Math.min(700, w * 0.75)));
            }
        };
        onResize();
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    const allDisorders = useMemo(() => {
        if (!rows) return [];
        return Array.from(new Set(rows.map(r => r.disorder).filter(Boolean))).sort();
    }, [rows]);

    const tree = useMemo(() => {
        if (!rows) return null;
        if (selectedLeaf) return buildDrugHierarchy(rows, selectedLeaf.drug);
        return buildHierarchy(rows, new Set<string>());
    }, [rows, selectedLeaf]);

    // Lookup: drug -> disorder -> targets (used by the focused detail view)
    const targetsByDrugDisorder = useMemo(() => {
        const m = new Map<string, Map<string, TargetInfo[]>>();
        if (!rows) return m;
        for (const r of rows) {
            if (!r.drug || !r.disorder) continue;
            if (!m.has(r.drug)) m.set(r.drug, new Map());
            const inner = m.get(r.drug)!;
            if (!inner.has(r.disorder)) inner.set(r.disorder, []);
            if (r.gene || r.target_name) {
                const arr = inner.get(r.disorder)!;
                const key = `${r.gene || ''}|${r.target_name || ''}`;
                if (!arr.find(t => `${t.gene || ''}|${t.target_name || ''}` === key)) {
                    arr.push({ gene: r.gene, target_name: r.target_name, uniprot: r.uniprot });
                }
            }
        }
        return m;
    }, [rows]);

    const cx = size / 2;
    const cy = size / 2;
    const radius = size / 2 - 80;

    const layoutData = useMemo(() => {
        if (!tree) return null;
        const { totalLeaves } = layoutRadial(tree, radius);
        const nodes: TreeNode[] = [];
        const walk = (n: TreeNode) => { nodes.push(n); n.children.forEach(walk); };
        walk(tree);
        const links: { p: TreeNode; c: TreeNode }[] = [];
        const collectLinks = (n: TreeNode) => {
            n.children.forEach(c => { links.push({ p: n, c }); collectLinks(c); });
        };
        collectLinks(tree);
        const leaves = nodes.filter(n => n.children.length === 0 && n.level > 0);
        const disorders = nodes.filter(n => n.level === 1);

        // Cross-disorder shared-drug links only make sense in the main tree.
        const drugGroups = new Map<string, TreeNode[]>();
        const crossLinks: { a: TreeNode; b: TreeNode; drug: string }[] = [];
        const sharedDrugs: string[] = [];
        if (!selectedLeaf) {
            for (const leaf of leaves) {
                if (!leaf.drug) continue;
                const arr = drugGroups.get(leaf.drug) || [];
                arr.push(leaf);
                drugGroups.set(leaf.drug, arr);
            }
            drugGroups.forEach((group, drug) => {
                if (group.length < 2) return;
                sharedDrugs.push(drug);
                for (let i = 0; i < group.length; i++) {
                    for (let j = i + 1; j < group.length; j++) {
                        crossLinks.push({ a: group[i], b: group[j], drug });
                    }
                }
            });
        }

        return { nodes, links, leaves, disorders, totalLeaves, drugGroups, crossLinks, sharedDrugs };
    }, [tree, radius, selectedLeaf]);

    const disorderColor = useCallback((d: string) => {
        const idx = allDisorders.indexOf(d);
        return DISORDER_PALETTE[idx % DISORDER_PALETTE.length];
    }, [allDisorders]);

    const applyTransform = useCallback(() => {
        if (!zoomGroupRef.current) return;
        const { k, x, y } = transformRef.current;
        zoomGroupRef.current.setAttribute(
            'transform',
            `translate(${cx + x}, ${cy + y}) scale(${k})`
        );
    }, [cx, cy]);

    useEffect(() => { applyTransform(); }, [applyTransform, layoutData]);

    useEffect(() => {
        const svg = svgRef.current;
        if (!svg) return;
        const handler = (e: WheelEvent) => {
            e.preventDefault();
            const t = transformRef.current;
            const scaleBy = e.deltaY < 0 ? 1.15 : 1 / 1.15;
            const newK = Math.max(0.5, Math.min(20, t.k * scaleBy));
            const rect = svg.getBoundingClientRect();
            const mx = e.clientX - rect.left - cx - t.x;
            const my = e.clientY - rect.top - cy - t.y;
            const k = newK / t.k;
            t.x -= mx * (k - 1);
            t.y -= my * (k - 1);
            t.k = newK;
            applyTransform();
        };
        svg.addEventListener('wheel', handler, { passive: false });
        return () => svg.removeEventListener('wheel', handler);
    }, [applyTransform, cx, cy, layoutData]);

    const resetView = () => {
        transformRef.current = { k: 1, x: 0, y: 0 };
        applyTransform();
    };

    const handleLeafClick = (leaf: TreeNode) => {
        if (selectedLeaf) {
            // In detail mode, leaves are targets — open the gene page.
            if (!leaf.gene || !leaf.disorder) return;
            const url = `/disorders/${encodeURIComponent(leaf.disorder)}?gene=${encodeURIComponent(leaf.gene)}`;
            window.open(url, '_blank', 'noopener,noreferrer');
            return;
        }
        if (!leaf.disorder || !leaf.drug) return;
        setSelectedLeaf({ drug: leaf.drug, disorder: leaf.disorder });
        transformRef.current = { k: 1, x: 0, y: 0 };
        applyTransform();
        setHover(null);
        setHighlightedDrug(null);
    };

    const exitDetail = () => {
        setSelectedLeaf(null);
        transformRef.current = { k: 1, x: 0, y: 0 };
        applyTransform();
        setHover(null);
    };

    const handleDisorderClick = (disorderName: string) => {
        const url = `/disorders/${encodeURIComponent(disorderName)}`;
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    const detailMeta = useMemo(() => {
        if (!selectedLeaf) return null;
        const drugMap = targetsByDrugDisorder.get(selectedLeaf.drug);
        const otherDisorders = drugMap
            ? Array.from(drugMap.keys()).filter(d => d !== selectedLeaf.disorder).sort()
            : [];
        const totalTargets = drugMap
            ? Array.from(drugMap.values()).reduce((acc, arr) => acc + arr.length, 0)
            : 0;
        return { otherDisorders, totalTargets };
    }, [selectedLeaf, targetsByDrugDisorder]);

    const showHoverFor = (e: React.MouseEvent, n: TreeNode) => {
        const rect = wrapperRef.current!.getBoundingClientRect();
        const lines: string[] = [];
        if (selectedLeaf) {
            // Drug-focused tree: level 1 = disorder, level 2 = target
            if (n.level === 1) lines.push(`Disorder · ${n.children.length} target${n.children.length === 1 ? '' : 's'}`);
            else if (n.level === 2) {
                lines.push('Target');
                if (n.target_name) lines.push(`Name: ${n.target_name}`);
                if (n.uniprot) lines.push(`UniProt: ${n.uniprot}`);
                if (n.disorder) lines.push(`Disorder: ${n.disorder}`);
            }
        } else {
            if (n.level === 1) lines.push(`Disorder · ${n.children.length} drugs`);
            else if (n.level === 2) {
                lines.push('Drug');
                if (n.disorder) lines.push(`Disorder: ${n.disorder}`);
                const group = n.drug ? layoutData?.drugGroups.get(n.drug) : undefined;
                if (group && group.length > 1) {
                    const others = group
                        .map(g => g.disorder)
                        .filter((d): d is string => !!d && d !== n.disorder);
                    if (others.length) lines.push(`Also in: ${others.join(', ')}`);
                }
            }
        }
        setHover({
            x: e.clientX - rect.left + 12,
            y: e.clientY - rect.top + 12,
            title: n.name,
            body: lines,
        });
    };

    return (
        <div ref={wrapperRef} style={{ width: '100%' }}>
            {/* Controls */}
            <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: '0.75rem',
                marginBottom: '0.5rem',
            }}>
                {selectedLeaf && (
                    <button
                        onClick={exitDetail}
                        style={{
                            fontSize: '0.8125rem',
                            padding: '0.35rem 0.75rem',
                            background: '#fff',
                            color: '#1A2940',
                            border: '1px solid #1A2940',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontWeight: 500,
                        }}
                    >
                        ← Back to tree
                    </button>
                )}
                <button
                    onClick={resetView}
                    style={{
                        fontSize: '0.8125rem',
                        padding: '0.35rem 0.75rem',
                        background: '#1A2940',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: 500,
                    }}
                >
                    Reset View
                </button>
            </div>

            {/* Stats line */}
            {selectedLeaf && detailMeta ? (
                <div style={{
                    fontSize: '0.8125rem',
                    color: '#6b7280',
                    marginBottom: '0.5rem',
                }}>
                    <strong style={{ color: '#1A2940' }}>{selectedLeaf.drug}</strong>
                    {' · '}{detailMeta.totalTargets} target{detailMeta.totalTargets === 1 ? '' : 's'} across {(detailMeta.otherDisorders.length + 1)} disorder{detailMeta.otherDisorders.length === 0 ? '' : 's'}
                    {detailMeta.otherDisorders.length > 0 && (
                        <> ({selectedLeaf.disorder}, {detailMeta.otherDisorders.join(', ')})</>
                    )}
                </div>
            ) : layoutData && (
                <div style={{
                    fontSize: '0.8125rem',
                    color: '#6b7280',
                    marginBottom: '0.5rem',
                }}>
                    {layoutData.disorders.length} disorders · {layoutData.drugGroups.size} unique drugs
                    {layoutData.sharedDrugs.length > 0 && (
                        <> · <span style={{ color: '#1A2940', fontWeight: 600 }}>{layoutData.sharedDrugs.length} shared across disorders</span></>
                    )}
                </div>
            )}

            {/* Tree canvas */}
            <div style={{
                position: 'relative',
                width: '100%',
                overflow: 'hidden',
                display: 'flex',
                justifyContent: 'center',
            }}>
                {loading && (
                    <div style={{
                        height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#6b7280',
                    }}>Loading tree data…</div>
                )}
                {error && !loading && (
                    <div style={{
                        height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#991b1b', flexDirection: 'column', gap: '0.5rem',
                    }}>
                        <div>Failed to load tree data</div>
                        <div style={{ fontSize: '0.8125rem', color: '#6b7280' }}>{error}</div>
                    </div>
                )}
                {layoutData && !loading && !error && (
                    <svg
                        ref={svgRef}
                        width={size}
                        height={size}
                        style={{ display: 'block', userSelect: 'none' }}
                        onMouseLeave={() => setHover(null)}
                    >
                        <g ref={zoomGroupRef} transform={`translate(${cx}, ${cy})`}>
                            {/* Disorder color arcs around outer rim */}
                            {layoutData.disorders.map(d => {
                                const leavesUnder: TreeNode[] = [];
                                const collect = (n: TreeNode) => {
                                    if (n.children.length === 0) leavesUnder.push(n);
                                    else n.children.forEach(collect);
                                };
                                collect(d);
                                if (leavesUnder.length === 0) return null;
                                const a0 = Math.min(...leavesUnder.map(l => l.angle!));
                                const a1 = Math.max(...leavesUnder.map(l => l.angle!));
                                const pad = Math.PI / (layoutData.totalLeaves * 2);
                                const active = highlightedDisorder === d.name;
                                return (
                                    <path
                                        key={`arc-${d.name}`}
                                        d={arcPath(radius + 6, active ? radius + 18 : radius + 14, a0 - pad, a1 + pad)}
                                        fill={disorderColor(d.name)}
                                        opacity={active ? 1 : 0.85}
                                        style={{ cursor: 'pointer', transition: 'opacity 120ms' }}
                                        onMouseEnter={() => setHighlightedDisorder(d.name)}
                                        onMouseLeave={() => setHighlightedDisorder(null)}
                                        onClick={(e) => { e.stopPropagation(); handleDisorderClick(d.name); }}
                                    />
                                );
                            })}

                            {/* Links */}
                            <g fill="none" stroke="#cbd5e1" strokeWidth={0.6} strokeOpacity={0.7}>
                                {layoutData.links.map((l, i) => {
                                    // root→disorder links belong to a disorder, disorder→leaf links belong to a specific drug/target leaf
                                    const isDisorderTrace = l.p.level === 0 && l.c.level === 1;
                                    const isLeafTrace = l.p.level === 1 && l.c.level === 2;
                                    const disorderOfLink = isDisorderTrace ? l.c.name : null;
                                    const leafOfLink = isLeafTrace ? l.c : null;

                                    const disorderActive = !!disorderOfLink && highlightedDisorder === disorderOfLink;
                                    const leafActive = !!leafOfLink?.drug && highlightedDrug === leafOfLink.drug && (!leafOfLink.disorder || highlightedDisorder === null || highlightedDisorder === leafOfLink.disorder);
                                    const active = disorderActive || leafActive;

                                    const stroke = l.p.level === 1 ? disorderColor(l.p.name)
                                        : l.c.disorder ? disorderColor(l.c.disorder)
                                        : '#cbd5e1';
                                    const baseW = l.p.level === 0 ? 1.6
                                        : l.p.level === 1 ? 1.0
                                        : l.p.level === 2 ? 0.6
                                        : 0.4;
                                    const w = active ? baseW + 0.8 : baseW;
                                    const baseOpacity = l.p.level <= 1 ? 0.55 : 0.35;
                                    const opacity = active ? Math.min(1, baseOpacity + 0.4) : baseOpacity;
                                    const path = radialLink(l.p, l.c);
                                    return (
                                        <g key={i}>
                                            <path
                                                d={path}
                                                stroke={stroke}
                                                strokeWidth={w}
                                                strokeOpacity={opacity}
                                                style={{ transition: 'stroke-opacity 120ms, stroke-width 120ms' }}
                                            />
                                            {/* root→disorder trace: hover highlights / click opens the disorder */}
                                            {disorderOfLink && (
                                                <path
                                                    d={path}
                                                    stroke="transparent"
                                                    strokeWidth={10}
                                                    style={{ cursor: 'pointer' }}
                                                    onMouseEnter={() => setHighlightedDisorder(disorderOfLink)}
                                                    onMouseLeave={() => setHighlightedDisorder(null)}
                                                    onClick={(e) => { e.stopPropagation(); handleDisorderClick(disorderOfLink); }}
                                                />
                                            )}
                                            {/* disorder→leaf trace: hover highlights the leaf / click opens its detail */}
                                            {leafOfLink && (
                                                <path
                                                    d={path}
                                                    stroke="transparent"
                                                    strokeWidth={10}
                                                    style={{ cursor: 'pointer' }}
                                                    onMouseEnter={() => { if (leafOfLink.drug) setHighlightedDrug(leafOfLink.drug); }}
                                                    onMouseLeave={() => setHighlightedDrug(null)}
                                                    onClick={(e) => { e.stopPropagation(); handleLeafClick(leafOfLink); }}
                                                />
                                            )}
                                        </g>
                                    );
                                })}
                            </g>

                            {/* Cross-disorder drug links (drugs shared by multiple disorders) */}
                            <g fill="none">
                                {layoutData.crossLinks.map((cl, i) => {
                                    const active = highlightedDrug === cl.drug;
                                    const d = bundledLink(cl.a, cl.b);
                                    return (
                                        <g key={`xl-${i}`}>
                                            {/* Visible line — barely there by default, pops on hover */}
                                            <path
                                                d={d}
                                                stroke={active ? '#f97316' : '#94a3b8'}
                                                strokeWidth={active ? 2.2 : 0.9}
                                                strokeOpacity={active ? 0.95 : 0.35}
                                                strokeLinecap="round"
                                                style={{ pointerEvents: 'none', transition: 'stroke-opacity 120ms, stroke-width 120ms' }}
                                            />
                                            {/* Invisible wide hit area for easier hovering + clicking */}
                                            <path
                                                d={d}
                                                stroke="transparent"
                                                strokeWidth={14}
                                                style={{ cursor: 'pointer' }}
                                                onMouseEnter={() => setHighlightedDrug(cl.drug)}
                                                onMouseLeave={() => setHighlightedDrug(null)}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (cl.a.drug && cl.a.disorder) handleLeafClick(cl.a);
                                                }}
                                            />
                                        </g>
                                    );
                                })}
                            </g>

                            {/* Drug leaves: dot + label */}
                            <g>
                                {layoutData.leaves.map((leaf, i) => {
                                    const [x, y] = polar(leaf.radius!, leaf.angle!);
                                    const color = leaf.disorder ? disorderColor(leaf.disorder) : '#94a3b8';
                                    const a = leaf.angle!;
                                    const deg = (a * 180) / Math.PI;
                                    const flip = a > Math.PI / 2 && a < (3 * Math.PI) / 2;
                                    const labelOffset = 8;
                                    const [lx, ly] = polar(leaf.radius! + labelOffset, a);
                                    const group = leaf.drug ? layoutData.drugGroups.get(leaf.drug) : undefined;
                                    const shared = !!group && group.length > 1;
                                    const active = !!leaf.drug && highlightedDrug === leaf.drug;
                                    return (
                                        <g key={i}>
                                            {shared && (
                                                <circle
                                                    cx={x}
                                                    cy={y}
                                                    r={active ? 6 : 5}
                                                    fill="none"
                                                    stroke={color}
                                                    strokeWidth={1}
                                                    strokeOpacity={active ? 0.95 : 0.55}
                                                    style={{ pointerEvents: 'none' }}
                                                />
                                            )}
                                            <circle
                                                cx={x}
                                                cy={y}
                                                r={active ? 4 : 3.2}
                                                fill={color}
                                                stroke="white"
                                                strokeWidth={0.6}
                                                style={{ pointerEvents: 'none' }}
                                            />
                                            {/* Invisible wide hit area for easier hovering + clicking */}
                                            <circle
                                                cx={x}
                                                cy={y}
                                                r={10}
                                                fill="transparent"
                                                style={{ cursor: 'pointer' }}
                                                onMouseEnter={(e) => {
                                                    showHoverFor(e, leaf);
                                                    if (shared && leaf.drug) setHighlightedDrug(leaf.drug);
                                                }}
                                                onMouseMove={(e) => showHoverFor(e, leaf)}
                                                onMouseLeave={() => { setHover(null); setHighlightedDrug(null); }}
                                                onClick={(e) => { e.stopPropagation(); handleLeafClick(leaf); }}
                                            />
                                            <text
                                                x={lx}
                                                y={ly}
                                                transform={flip
                                                    ? `rotate(${deg + 180}, ${lx}, ${ly})`
                                                    : `rotate(${deg}, ${lx}, ${ly})`}
                                                textAnchor={flip ? 'end' : 'start'}
                                                dominantBaseline="middle"
                                                fontSize={11}
                                                fontWeight={active ? 700 : 400}
                                                fill={active ? '#1A2940' : '#1f2937'}
                                                style={{ pointerEvents: 'none', fontFamily: 'system-ui, sans-serif' }}
                                            >
                                                {leaf.name}
                                            </text>
                                        </g>
                                    );
                                })}
                            </g>

                            {/* Disorder labels (level 1) — big, on inner ring */}
                            <g>
                                {layoutData.disorders.map((d, i) => {
                                    const a = d.angle!;
                                    const r = d.radius! - 6;
                                    const deg = (a * 180) / Math.PI;
                                    const flip = a > Math.PI / 2 && a < (3 * Math.PI) / 2;
                                    const [x, y] = polar(r, a);
                                    const active = highlightedDisorder === d.name;
                                    return (
                                        <text
                                            key={i}
                                            x={x}
                                            y={y}
                                            transform={flip
                                                ? `rotate(${deg + 180}, ${x}, ${y})`
                                                : `rotate(${deg}, ${x}, ${y})`}
                                            textAnchor={flip ? 'end' : 'start'}
                                            dominantBaseline="middle"
                                            fontSize={active ? 12 : 11}
                                            fontWeight={700}
                                            fill={disorderColor(d.name)}
                                            style={{
                                                cursor: 'pointer',
                                                fontFamily: 'system-ui, sans-serif',
                                                textDecoration: active ? 'underline' : 'none',
                                                transition: 'font-size 120ms',
                                            }}
                                            onMouseEnter={(e) => { showHoverFor(e, d); setHighlightedDisorder(d.name); }}
                                            onMouseMove={(e) => showHoverFor(e, d)}
                                            onMouseLeave={() => { setHover(null); setHighlightedDisorder(null); }}
                                            onClick={(e) => { e.stopPropagation(); handleDisorderClick(d.name); }}
                                        >
                                            {d.name}
                                        </text>
                                    );
                                })}
                            </g>

                            {/* Center label */}
                            <text
                                x={0}
                                y={0}
                                textAnchor="middle"
                                dominantBaseline="middle"
                                fontSize={selectedLeaf ? (selectedLeaf.drug.length > 14 ? 11 : 13) : 14}
                                fontWeight={700}
                                fill="#1A2940"
                                style={{ pointerEvents: 'none', fontFamily: 'system-ui, sans-serif' }}
                            >
                                {selectedLeaf
                                    ? (selectedLeaf.drug.length > 18
                                        ? selectedLeaf.drug.substring(0, 16) + '…'
                                        : selectedLeaf.drug)
                                    : 'MintDB'}
                            </text>
                        </g>
                    </svg>
                )}

                {/* Tooltip */}
                {hover && (
                    <div
                        style={{
                            position: 'absolute',
                            left: hover.x,
                            top: hover.y,
                            background: 'rgba(26,41,64,0.96)',
                            color: 'white',
                            padding: '0.5rem 0.75rem',
                            borderRadius: '6px',
                            fontSize: '0.75rem',
                            pointerEvents: 'none',
                            maxWidth: 280,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                            zIndex: 10,
                        }}
                    >
                        <div style={{ fontWeight: 700, marginBottom: 2 }}>{hover.title}</div>
                        {hover.body.map((l, i) => (
                            <div key={i} style={{ opacity: 0.9 }}>{l}</div>
                        ))}
                    </div>
                )}
            </div>

            {/* Legend / help */}
            <div style={{
                fontSize: '0.75rem',
                color: '#6b7280',
                marginTop: '0.5rem',
                display: 'flex',
                gap: '1rem',
                flexWrap: 'wrap',
            }}>
                {selectedLeaf ? (
                    <span>Scroll to zoom · click a target leaf to open its gene page · use ← Back to tree to return</span>
                ) : (
                    <span>Scroll to zoom · click a drug to focus on it · ringed dots are drugs shared across disorders (hover to highlight the link)</span>
                )}
            </div>
        </div>
    );
};

export default MintDBTree;
